import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const IDLE_MINUTES = 4 * 60;            // 4 hours of no activity
const IDLE_MS = IDLE_MINUTES * 60 * 1000;
const STORAGE_KEY = "pos_last_activity";
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];

/**
 * Auto-logout after 4 hours of no user activity.
 *
 * - Tracks the timestamp of the last user input in localStorage so the timer
 *   survives soft refreshes / tab switches.
 * - Throttles the storage write to once every 30s — moving the mouse should
 *   not hammer localStorage on every pixel.
 * - Checks every 60s; when the gap exceeds IDLE_MS, calls `logout()` and
 *   redirects to `/login` with a friendly toast explaining why.
 *
 * Mounted inside <Layout> so it's only active for authenticated sessions.
 */
export default function IdleLogout() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const lastWrite = useRef(0);

  useEffect(() => {
    // Mark activity on mount so a fresh login resets the timer.
    const now = Date.now();
    localStorage.setItem(STORAGE_KEY, String(now));
    lastWrite.current = now;

    const bumpActivity = () => {
      const t = Date.now();
      if (t - lastWrite.current < 30000) return;   // throttle to 30s
      lastWrite.current = t;
      localStorage.setItem(STORAGE_KEY, String(t));
    };

    ACTIVITY_EVENTS.forEach((ev) => {
      window.addEventListener(ev, bumpActivity, { passive: true });
    });

    const tick = () => {
      const last = Number(localStorage.getItem(STORAGE_KEY) || 0);
      if (!last) {
        localStorage.setItem(STORAGE_KEY, String(Date.now()));
        return;
      }
      if (Date.now() - last > IDLE_MS) {
        // Idle timeout reached — sign out.
        ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, bumpActivity));
        clearInterval(interval);
        toast.warning("You've been signed out after 4 hours of inactivity.");
        logout();
        nav("/login");
      }
    };
    const interval = setInterval(tick, 60_000);   // check every minute

    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, bumpActivity));
      clearInterval(interval);
    };
  }, [logout, nav]);

  return null;
}
